// 도감 2.0 장부(dungeonhero.codex.v1)의 동기화 병합 — 서버(저장)와 클라이언트(빨리감기·부팅 적용)가 같은 규칙·같은 직렬화를 쓴다.
// 순수 모듈: 다른 import 없음(Worker가 account/keys.ts를 통해 가져오므로 게임·레지스트리를 끌어오지 않는다).
//
// 왜 병합인가(PR #397 리뷰 P1): 도감 처치 수는 칸별로 단조 증가하는 장부라 두 기기의 값을 '되돌리거나 한쪽으로 덮지' 않고 합치는 것이 맞다.
// 서버가 위반이라며 이전 값으로 되돌리면 두 기기 LWW에서 한쪽이 영구 고착되고, 그 뒤 빨리감기 적용이 오프라인 처치 수를 로컬에서 지웠다.
// 병합: 칸별 max · 영수증 합집합(최근 256, 뒷쪽 = 새 값 우선) · n/t max. 직렬화는 정규(칸 id 정렬·필드 순서 고정)라
// 같은 내용이면 같은 원문이 되어, 병합 결과를 받은 기기가 '로컬 변경'으로 오판해 되올리는 핑퐁이 생기지 않는다.

/** 칸 id 패턴: 몬스터 `m:…`, 보스·레이드 `b:…`. 레지스트리 Set 대신 패턴을 써서 앞으로 칸이 늘어도(다음 빌드) 옛 서버가 거절하지 않는다 */
export const CODEX_CELL_ID = /^[mb]:[a-z0-9_:]{1,48}$/;
/** 칸 하나의 처치 수 상한 */
export const CODEX_CELL_MAX = 10_000_000;
/** 영수증 id 보관 수(최근 순)·길이 */
export const CODEX_RECEIPTS_CAP = 256;
export const CODEX_RECEIPT_ID_MAX = 128;
/** 칸 수 상한(패턴만 맞는 칸으로 원문을 부풀리지 못하게 — 지금 37칸, 넉넉히) */
export const CODEX_CELLS_MAX = 512;

type Obj = Record<string, unknown>;
const obj = (v: unknown): v is Obj => v !== null && typeof v === 'object' && !Array.isArray(v);
const nat = (v: unknown, max: number): v is number => typeof v === 'number' && Number.isSafeInteger(v) && v >= 0 && v <= max;

/** 검사를 통과한 도감 문서. s0 = 서버가 처음 받아들인 시각(ms, 서버만 찍는다). extra = 미래 판본(v>1)의 모르는 최상위 필드(그대로 보존) */
export interface CodexDoc { v: number; k: Record<string, number>; r: string[]; n: number; t: number; s0?: number; extra?: Obj }

const STD_FIELDS = new Set(['v', 'k', 'r', 'n', 't', 's0']);

/**
 * 형식 검사 + 문서화. 틀리면 사유 문자열, 통과하면 문서. 미래 판본(v>1)도 k·r·n·t는 v1 규칙으로 검사한다(형식이 다르면 위반).
 * s0는 클라이언트가 보낸 값을 믿지 않으므로 여기서는 숫자일 때만 읽고(서버는 쓰지 않는다), 병합 쪽이 고른다.
 */
export function parseCodexDoc(v: Obj): CodexDoc | string {
  if (typeof v.v !== 'number' || !Number.isInteger(v.v) || v.v < 1) return 'codex-version';
  if (!obj(v.k)) return 'codex-shape';
  const k: Record<string, number> = {};
  let cells = 0;
  for (const [id, c] of Object.entries(v.k)) {
    // 프로토타입 키(__proto__·constructor 등)는 패턴(m:·b: 접두)을 통과하지 못한다
    if (!CODEX_CELL_ID.test(id) || ++cells > CODEX_CELLS_MAX) return 'codex-cell';
    if (!nat(c, CODEX_CELL_MAX)) return 'codex-count';
    k[id] = c;
  }
  const r = v.r;
  if (!Array.isArray(r) || r.length > CODEX_RECEIPTS_CAP || !r.every((x) => typeof x === 'string' && x.length <= CODEX_RECEIPT_ID_MAX)) return 'codex-receipts';
  if (!nat(v.n, Number.MAX_SAFE_INTEGER)) return 'codex-n';
  if (!nat(v.t, Number.MAX_SAFE_INTEGER)) return 'codex-t';
  const doc: CodexDoc = { v: v.v, k, r: r as string[], n: v.n, t: v.t };
  if (nat(v.s0, Number.MAX_SAFE_INTEGER)) doc.s0 = v.s0;
  if (v.v > 1) {
    const extra: Obj = {};
    for (const [f, val] of Object.entries(v)) if (!STD_FIELDS.has(f)) extra[f] = val;
    if (Object.keys(extra).length > 0) doc.extra = extra;
  }
  return doc;
}

/** 원문 → 문서(JSON·형식이 틀리면 null). 클라이언트 병합용 */
export function readCodexDoc(raw: string): CodexDoc | null {
  try { const v: unknown = JSON.parse(raw); if (!obj(v)) return null; const d = parseCodexDoc(v); return typeof d === 'string' ? null : d; } catch { return null; }
}

/** 정규 직렬화: v·k(칸 id 정렬)·r·n·t·s0 순서 고정 + 미래 판본의 모르는 필드. 같은 내용이면 항상 같은 원문 */
export function serializeCodexDoc(d: CodexDoc): string {
  const k: Record<string, number> = {};
  for (const id of Object.keys(d.k).sort()) k[id] = d.k[id];
  const o: Obj = { v: d.v, k, r: d.r, n: d.n, t: d.t };
  if (d.s0 !== undefined) o.s0 = d.s0;
  if (d.extra) for (const [f, val] of Object.entries(d.extra)) o[f] = val;
  return JSON.stringify(o);
}

/**
 * 두 문서를 합친다(s0는 호출자가 정한다 — 서버는 저장된 값, 클라이언트는 서버 값):
 * 칸별 max · 영수증 합집합(a에만 있는 것 뒤에 b 순서 그대로, 최근 256 — b가 새 값이라 잘릴 때 b가 남는다) · n max · t max · v max.
 * 미래 판본의 모르는 필드는 더 높은 판본(같으면 b)의 것을 따른다.
 * 칸 수는 합치면 늘 수 있다(512 + 512 → 최대 1024) — 저장·적용 전에 capCodexCells로 자른다(자르지 않으면 parseCodexDoc이 다음 읽기를 거절한다).
 *
 * v 래칫(P3, 동작 고정 — 시험 `P3: v 래칫` 이 묶는다): v = max(a.v, b.v)라 서버에 v=2 문서가 한 번 저장되면 병합 결과의 v도 계속 2 이상이다.
 * 이 빌드(v=1)의 CodexStore는 v>1 원문을 '미래 판본'으로 보고 읽기 전용이 된다(처치 수를 더 쌓지 못한다) — 그래서 v=2 문서를 만드는 빌드를 내기 전에 정할 것:
 *  1) v2 빌드가 쓰는 문서에 v1 필드(k·r·n·t)를 그대로 두면 v1 빌드는 그 문서를 읽기만 한다(롤백 안전은 지켜지지만 v1 탭이 열려 있던 기기는 도감 진행이 멈춘다).
 *     '읽기 전용이 되어도 괜찮은가'를 먼저 결정하고, 아니면 v2를 v1 CodexStore가 쓸 수 있는 형태로(v는 1로 두고 새 필드는 extra로) 시작한다.
 *  2) v 래칫은 의도다: v1 기기가 v2를 v1로 되돌려 쓰지 못하게 한다. 되돌리는 경로(v를 낮추는 병합)를 만들지 않는다.
 *  3) 모르는 최상위 필드(extra)는 v>1일 때만 서버가 보존한다 — v2 필드는 반드시 최상위에 두고 parseCodexDoc이 v1 규칙(k·r·n·t)으로 검사하는 모양을 유지한다.
 */
export function mergeCodexDocs(a: CodexDoc, b: CodexDoc): CodexDoc {
  const k: Record<string, number> = {};
  for (const id of Object.keys(a.k)) k[id] = a.k[id];
  for (const id of Object.keys(b.k)) k[id] = Math.max(Object.hasOwn(k, id) ? k[id] : 0, b.k[id]);
  const inB = new Set(b.r), seen = new Set<string>(), r: string[] = [];
  for (const id of a.r) if (!inB.has(id) && !seen.has(id)) { seen.add(id); r.push(id); }
  for (const id of b.r) if (!seen.has(id)) { seen.add(id); r.push(id); }
  const out: CodexDoc = { v: Math.max(a.v, b.v), k, r: r.slice(-CODEX_RECEIPTS_CAP), n: Math.max(a.n, b.n), t: Math.max(a.t, b.t) };
  const extra = b.v >= a.v ? b.extra ?? a.extra : a.extra ?? b.extra;
  if (extra && out.v > 1) out.extra = extra;
  return out;
}

/**
 * 칸 수가 CODEX_CELLS_MAX를 넘으면 자른다(병합 결과 정리용, 결정적). keep(레지스트리 칸)은 전부 유지하고, 나머지(모르는 칸)는
 * 처치 수가 큰 순 → 같으면 id 사전순으로 남은 자리만 채운다. keep만으로 넘치는 비정상 입력도 같은 순서로 자른다. 넘지 않으면 문서를 그대로 돌려준다
 */
export function capCodexCells(d: CodexDoc, keep: ReadonlySet<string>): CodexDoc {
  const ids = Object.keys(d.k);
  if (ids.length <= CODEX_CELLS_MAX) return d;
  const rank = (x: string, y: string): number => d.k[y] - d.k[x] || (x < y ? -1 : x > y ? 1 : 0);
  const kept = ids.filter((id) => keep.has(id)).sort(rank).slice(0, CODEX_CELLS_MAX);
  const rest = ids.filter((id) => !keep.has(id)).sort(rank).slice(0, CODEX_CELLS_MAX - kept.length);
  const k: Record<string, number> = {};
  for (const id of [...kept, ...rest]) k[id] = d.k[id];
  return { ...d, k };
}

/** 칸 처치 수의 합(모르는 칸 포함) */
export function codexTotal(d: CodexDoc): number {
  let s = 0;
  for (const id of Object.keys(d.k)) s += d.k[id];
  return s;
}

/**
 * 클라이언트 적용용 병합(writeSnapshot): 로컬 원문과 서버 원문을 합친 정규 원문. 어느 쪽이든 읽을 수 없으면 서버 원문 그대로.
 * s0는 서버 값(없으면 로컬 값 — 클라이언트는 s0를 만들지 않는다). 서버가 로컬을 이미 포함하면 결과 원문은 서버 원문과 같다
 */
export function mergeCodexRaw(localRaw: string | null, serverRaw: string, keep: ReadonlySet<string> = new Set()): string {
  if (localRaw === null || localRaw === serverRaw) return serverRaw;
  const a = readCodexDoc(localRaw), b = readCodexDoc(serverRaw);
  if (!a || !b) return serverRaw;
  // t는 서버 저장본 값을 쓴다(PR #397 재리뷰 P2): 서버는 t를 now로 자르는데 여기서 max(로컬, 서버)를 쓰면 시계가 앞선 기기의 로컬 t가 남아
  // 저장본과 영영 달라져 매 동기화 재업로드·상대 기기 staged가 반복된다. 로컬 탭끼리 합치기(codexStore)는 그대로 max
  const m = capCodexCells(mergeCodexDocs(a, b), keep);
  m.t = b.t;
  const s0 = b.s0 ?? a.s0;
  if (s0 !== undefined) m.s0 = s0;
  return serializeCodexDoc(m);
}

/**
 * got(적용 뒤 로컬)가 expected(서버 값)를 포함하는가 — 칸 처치 수와 n이 모두 이상이면 true. 읽을 수 없으면 원문이 같을 때만.
 * writeSnapshot이 로컬 처치 수를 서버 값에 합쳐 쓰므로, 적용 검증(appliedMatches)이 원문 동일 대신 이것으로 본다
 */
export function codexCovers(gotRaw: string, expectedRaw: string): boolean {
  if (gotRaw === expectedRaw) return true;
  const g = readCodexDoc(gotRaw), e = readCodexDoc(expectedRaw);
  if (!g || !e) return false;
  if (g.n < e.n) return false;
  for (const id of Object.keys(e.k)) if ((Object.hasOwn(g.k, id) ? g.k[id] : 0) < e.k[id]) return false;
  return true;
}
